import constants from '../../../constants.js';
import { priceCents, safeColor } from '../cms-market-helpers.js';

const DAY = 86400000;
const HISTORY_DAYS = 30;
const WEEK_DAYS = 7;
const EDGE_MARGIN_CENTS = 5;
const MIN_TEXT_CONTRAST = 4.5;
const COLOR_BLEND_STEPS = 20;

// Chart API date-only / timezone-less daily buckets are treated as UTC calendar dates.
function historyTime(value) {
  if (typeof value !== 'string' || !value) return NaN;
  return Date.parse(
    /T.*(?:Z|[+-]\d\d:\d\d)$/i.test(value) ? value : `${value.replace(/T.*$/, '')}T00:00:00Z`,
  );
}

function closedHistory(sides, records) {
  const series = sides.map((side) => {
    const unique = new Map();
    for (const record of Array.isArray(records) ? records : []) {
      if (!record || Number(record.contractId) !== side.id) continue;
      const date = typeof record.date === 'string' ? record.date.split('T')[0] : '';
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
      const calendarDay = new Date(`${date}T00:00:00Z`);
      if (
        !Number.isFinite(calendarDay.getTime()) ||
        calendarDay.toISOString().split('T')[0] !== date
      )
        continue;
      const time = historyTime(record.date);
      if (!Number.isFinite(time)) continue;
      unique.set(time, { time, price: priceCents(record.closeSharePrice) });
    }
    const points = [...unique.values()].sort((a, b) => a.time - b.time);
    const endpoint = points.filter((point) => point.price !== null).at(-1);
    return { points, endpoint };
  });
  const hasRange = series.some((item) => item.endpoint);
  const times = hasRange ? series.flatMap((item) => item.points.map((point) => point.time)) : [];
  return {
    series,
    hasRange,
    start: times.length ? Math.min(...times) : null,
    end: times.length ? Math.max(...times) : null,
  };
}

export function buildSummary(tile, records, now = Date.now()) {
  const state = [constants.MARKET_CLOSED, constants.MARKET_SETTLED].includes(tile.marketStatus)
    ? tile.marketStatus
    : constants.MARKET_OPEN;
  const isOpen = state === constants.MARKET_OPEN;
  const today = new Date(now);
  const sides = [tile.left, tile.right];
  const history = isOpen ? null : closedHistory(sides, records);
  const start = isOpen
    ? Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()) - HISTORY_DAYS * DAY
    : history.start;
  const settled = state === constants.MARKET_SETTLED;
  const winner = settled
    ? sides.find((side, index) => side.isWinner === true && sides[1 - index].isWinner === false) ||
      null
    : null;
  const loser = winner ? sides.find((side) => side.id !== winner.id) : null;
  const known = sides.every((side) => side.price !== null);
  const tied = known && tile.left.price === tile.right.price;
  const leader =
    known && !tied ? (tile.left.price > tile.right.price ? tile.left : tile.right) : null;
  const trailer = leader ? sides.find((side) => side.id !== leader.id) : null;
  const series = sides.map((side, index) => {
    if (!isOpen) {
      const { points, endpoint } = history.series[index];
      const current = endpoint?.price ?? null;
      return {
        ...side,
        price: current,
        current,
        display: current === null ? '—' : `${current}${tile.currentPriceSymbol}`,
        endpointTime: endpoint?.time ?? null,
        isWinner: winner?.id === side.id,
        isDefeated: settled && side.isWinner === false,
        weeklyChange: null,
        hasHistory: !!endpoint,
        points,
      };
    }
    const unique = new Map();
    for (const record of Array.isArray(records) ? records : []) {
      if (Number(record.contractId) !== side.id) continue;
      const time = historyTime(record.date);
      if (!Number.isFinite(time) || time < start || time > now) continue;
      unique.set(time, { time, price: priceCents(record.closeSharePrice) });
    }
    const historical = [...unique.values()].sort((a, b) => a.time - b.time);
    const validHistory = historical.filter((point) => point.price !== null);
    const baseline =
      validHistory.filter((point) => point.time <= now - WEEK_DAYS * DAY).at(-1) || validHistory[0];
    const weeklyChange =
      validHistory.length >= 2 && side.price !== null ? side.price - baseline.price : null;
    const hasHistory = validHistory.length > 0;
    // Live data is an overlay, not a mutation of the fetched daily-close snapshot.
    const points = historical.filter((point) => point.time < now);
    if (side.price !== null) points.push({ time: now, price: side.price });
    return {
      ...side,
      current: side.price,
      weeklyChange,
      hasHistory,
      points,
    };
  });
  return {
    state,
    winner,
    loser,
    neitherWins: settled && sides.every((side) => side.isWinner === false),
    hasRange: isOpen ? series.some((item) => item.hasHistory) : history.hasRange,
    start,
    now: isOpen ? now : history.end,
    series,
    leader,
    trailer,
    tied,
    verb: known
      ? tied
        ? 'tied'
        : Math.abs(tile.left.price - tile.right.price) < EDGE_MARGIN_CENTS
          ? 'edges'
          : 'leads'
      : null,
    difference: known
      ? `${Math.abs(tile.left.price - tile.right.price)}${tile.currentPriceSymbol}`
      : '',
  };
}

// Preserve the CMS hue, moving only toward black/white when needed for AA text.
export function readableContractColor(value, dark) {
  const channels = safeColor(value)
    .slice(1)
    .match(/../g)
    .map((part) => parseInt(part, 16));
  const luminance = (rgb) =>
    rgb
      .map((v) => {
        const n = v / 255;
        return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
      })
      .reduce((sum, n, i) => sum + n * [0.2126, 0.7152, 0.0722][i], 0);
  const background = luminance(dark ? [37, 50, 64] : [255, 255, 255]);
  let result = channels;
  for (let step = 0; step <= COLOR_BLEND_STEPS; step += 1) {
    result = channels.map((n) =>
      Math.round(n + (((dark ? 255 : 0) - n) * step) / COLOR_BLEND_STEPS),
    );
    const light = luminance(result);
    if (
      (Math.max(light, background) + 0.05) / (Math.min(light, background) + 0.05) >=
      MIN_TEXT_CONTRAST
    )
      break;
  }
  return `#${result.map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}
